import CloseIcon from '@mui/icons-material/Close'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import { selectEmrSectionsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { fetchEmrSections } from 'src/clinic/reducers/prescription/services/emrSectionThunk'
import AssessmentReportBody, {
  AssessmentReportFooter,
} from 'src/mentor/components/AssessmentReport/viewComponents/AssessmentReportBody'
import ViewOnlyPrescriptionV2 from 'src/mentor/components/PrescriptionMakingPage/viewOnlyComponents/ViewOnlyPrescriptionV2'
import { hasMeaningfulPrescriptionData } from 'src/mentor/components/PrescriptionMakingPage/printComponents/utils/emrReportContentUtils'
import PrintableScreeningReportComponent from 'src/mentor/components/ScreeningReportMakingPage/printableReport/PrintableScreeningReportComponent'
import TherapyReportPreview from 'src/mentor/components/TherapyReportMakingPage/components/TherapyReportPreview'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import {
  getSupportReportAccessConfig,
} from 'src/supportPortal/access/accessMatrix'
import { getSPDataWithParams } from 'src/supportPortal/services/services'

const DRAWER_WIDTH = 920

function formatWhen(value) {
  if (!value) {
    return ''
  }
  const date = dayjs(value)
  if (!date.isValid()) {
    return ''
  }
  return date.format('DD MMM YYYY, hh:mm A')
}

function buildReadableError(response, fallbackMessage) {
  const payload = response?.data || response?.response?.data || {}
  return payload?.message || fallbackMessage
}

function HeaderChip({ label, color = 'default' }) {
  if (!label) {
    return null
  }

  return <Chip size="small" label={label} color={color} variant="outlined" />
}

function ReportShell({ title, subtitle, children }) {
  return (
    <Box
      sx={{
        maxWidth: '880px',
        mx: 'auto',
        width: '100%',
        borderRadius: 3,
        backgroundColor: '#ffffff',
        boxShadow: '0 22px 48px rgba(15, 23, 42, 0.08)',
        border: '1px solid rgba(148, 163, 184, 0.14)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          background:
            'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(99, 102, 241, 0.1) 100%)',
          borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" sx={{ mt: 0.75, color: 'rgba(15, 23, 42, 0.72)' }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>{children}</Box>
    </Box>
  )
}

function isImageUrl(url) {
  const path = String(url || '').split('?')[0].toLowerCase()
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(path)
}

// Renders the prescription PDF(s)/scan(s) that were uploaded against this booking (e.g. EkaCare
// bulk-imported prescriptions). The structured pad below stays blank for these, so without this the
// uploaded document is never visible.
function UploadedPrescriptionFiles({ urls }) {
  if (!Array.isArray(urls) || urls.length === 0) {
    return null
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      {urls.map((url, idx) => (
        <Box
          key={`${url}-${idx}`}
          sx={{ border: '1px solid rgba(148, 163, 184, 0.3)', borderRadius: 2, overflow: 'hidden' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              backgroundColor: 'rgba(148, 163, 184, 0.08)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Uploaded prescription{urls.length > 1 ? ` (${idx + 1}/${urls.length})` : ''}
            </Typography>
            <Typography
              component="a"
              href={url}
              target="_blank"
              rel="noreferrer"
              variant="body2"
              sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600 }}
            >
              Open in new tab
            </Typography>
          </Box>
          {isImageUrl(url) ? (
            <Box component="img" src={url} alt="Uploaded prescription" sx={{ width: '100%', display: 'block' }} />
          ) : (
            <Box
              component="iframe"
              src={url}
              title={`Uploaded prescription ${idx + 1}`}
              sx={{ width: '100%', height: { xs: 480, sm: 760 }, border: 0, display: 'block' }}
            />
          )}
        </Box>
      ))}
    </Box>
  )
}


export default function SupportReportViewDrawer({ open, onClose, bookedEvent }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [bookedEventInfo, setBookedEventInfo] = useState(null)
  const [auxiliaryData, setAuxiliaryData] = useState(null)
  const [emrSectionDataList, setEmrSectionDataList] = useState([])
  const [rxTab, setRxTab] = useState('uploaded')

  const reportConfig = useMemo(
    () => getSupportReportAccessConfig(bookedEvent?.eventType),
    [bookedEvent?.eventType],
  )

  useEffect(() => {
    let active = true

    async function fetchReport() {
      if (!open || (!bookedEvent?.slug && !bookedEvent?.id)) {
        return
      }

      setLoading(true)
      setError('')

      const response = await getSPDataWithParams(
        '/api/secure/cc/support/practitioner-supervisor/booked-slot/details',
        {
          ...(bookedEvent?.id ? { eventId: bookedEvent.id } : { slug: bookedEvent.slug }),
          'send-auxiliary-data': true,
        },
      )

      if (!active) {
        return
      }

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const data = getDataMapFromCommonResponse(response)
        setBookedEventInfo(data?.bookedEvent || null)
        setAuxiliaryData(data || null)
        setEmrSectionDataList(
          data?.screening__EmrSectionData || data?.emrSectionDataList || data?.emrSectionData || [],
        )
        setLoading(false)
        return
      }

      setError(buildReadableError(response, 'Unable to load this report right now.'))
      setLoading(false)
    }

    if (open) {
      fetchReport()
    } else {
      setLoading(false)
      setError('')
      setBookedEventInfo(null)
      setAuxiliaryData(null)
      setEmrSectionDataList([])
      setRxTab('uploaded')
    }

    return () => {
      active = false
    }
  }, [bookedEvent?.id, bookedEvent?.slug, open])

  const resolvedBookedEvent = bookedEventInfo || bookedEvent || {}
  const prescriptionData = auxiliaryData?.metaPrescription || null

  // Same layout source as the prescription making page (specialization-aware EMR section
  // resolution), fetched for the booking's practitioner via the support variant of the thunk.
  // forceUpdateSections because the store slot is shared across practitioners.
  const dispatch = useDispatch()
  const emrSectionsStoreData = useSelector(selectEmrSectionsData)
  const doctorConfiguredEmrSections =
    emrSectionsStoreData?.data?.prescription?.emrSections || null
  const reportPractitionerId = resolvedBookedEvent?.mentorId
  useEffect(() => {
    if (open && reportPractitionerId) {
      dispatch(fetchEmrSections({ practitionerId: reportPractitionerId, forceUpdateSections: true }))
    }
  }, [open, reportPractitionerId, dispatch])
  const therapyReportData = auxiliaryData?.therapyReport
    ? {
        report: auxiliaryData?.therapyReport,
        template: auxiliaryData?.therapyReportTemplate,
      }
    : null
  const screeningReportData = auxiliaryData?.screeningReport
    ? {
        report: auxiliaryData?.screeningReport,
        template: auxiliaryData?.screeningReportTemplate,
      }
    : null
  const assessmentReportData = auxiliaryData?.assessmentReport
    ? {
        report: auxiliaryData?.assessmentReport,
        template: auxiliaryData?.assessmentReportTemplate || auxiliaryData?.therapyReportTemplate,
      }
    : null

  const reportTitle = reportConfig?.title || 'View Report'
  const reportType = reportConfig?.reportType || ''
  const eventTypeLabel =
    displayNamesOfEventTypes?.[resolvedBookedEvent?.eventType] || resolvedBookedEvent?.eventType || ''
  const clinicLabel = [resolvedBookedEvent?.clinicBrandName, resolvedBookedEvent?.clinicName]
    .filter(Boolean)
    .join(' | ')

  const renderReport = () => {
    if (reportType === 'prescription') {
      if (!prescriptionData) {
        return (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No prescription is available for this booking yet.
          </Alert>
        )
      }

      const innerRx = prescriptionData?.prescriptionData || {}
      const uploadedUrls = innerRx?.uploadedSignedImages || []
      const hasUploaded = uploadedUrls.length > 0
      const hasGenerated = hasMeaningfulPrescriptionData(innerRx)
      // The generated (structured) pad always renders now — ViewOnlyPrescriptionV2 falls back to a
      // default section layout when the prescription carries no EMR sections (e.g. imports).
      // doctorEmrSections must be the section LAYOUT (defs), never the saved emrSectionData
      // records — those poison the layout and the pad renders blank.
      // A4 print preview, same as the making page's Quick Print drawer.
      const generatedPad = (
        <Box sx={{ overflowX: 'auto', backgroundColor: '#f5f5f5', p: 2, borderRadius: 2 }}>
          <Box
            sx={{
              minWidth: '750px',
              margin: '0 auto',
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <ViewOnlyPrescriptionV2
              prescriptionData={prescriptionData}
              doctorEmrSections={
                doctorConfiguredEmrSections?.length
                  ? doctorConfiguredEmrSections
                  : auxiliaryData?.doctorEmrSections
              }
              showTheA4Page
              hidePrintMarginsControl={true}
              practitionerId={resolvedBookedEvent?.mentorId}
            />
          </Box>
        </Box>
      )

      // Tabs only when there's an uploaded document to switch between; otherwise just the pad.
      if (!hasUploaded) {
        return (
          <ReportShell title="Prescription" subtitle="Read-only consultation prescription">
            {generatedPad}
          </ReportShell>
        )
      }

      // Uploaded-only record (no structured prescription data): show just the uploaded document —
      // don't surface an empty Generated tab.
      if (!hasGenerated) {
        return (
          <ReportShell title="Prescription" subtitle="Read-only consultation prescription">
            <UploadedPrescriptionFiles urls={uploadedUrls} />
          </ReportShell>
        )
      }

      const activeTab = rxTab === 'generated' ? 'generated' : 'uploaded'
      return (
        <ReportShell title="Prescription" subtitle="Read-only consultation prescription">
          <Tabs
            value={activeTab}
            onChange={(_, value) => setRxTab(value)}
            sx={{ mb: 2, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
          >
            <Tab value="uploaded" label="Uploaded" />
            <Tab value="generated" label="Generated" />
          </Tabs>
          {activeTab === 'uploaded' ? <UploadedPrescriptionFiles urls={uploadedUrls} /> : generatedPad}
        </ReportShell>
      )
    }

    if (reportType === 'screening-report') {
      if (!screeningReportData) {
        return (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No screening report is available for this booking yet.
          </Alert>
        )
      }

      return (
        <ReportShell title="Screening Report" subtitle="Read-only support view">
          <PrintableScreeningReportComponent
            emrSectionDataList={emrSectionDataList}
            bookedEventInfo={resolvedBookedEvent}
            templateData={screeningReportData?.template}
            isAlwaysPrinting
            report={screeningReportData?.report}
            hideDownloadButton={true}
            hideSendToDoctorButton={true}
            hideHeader={true}
            hideHeadingText={true}
          />
        </ReportShell>
      )
    }

    if (reportType === 'therapy-report') {
      if (!therapyReportData) {
        return (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No therapy report is available for this booking yet.
          </Alert>
        )
      }

      return (
        <ReportShell title="Therapy Report" subtitle="Read-only support view">
          <TherapyReportPreview
            hideInUI={false}
            isAlwaysPrinting
            report={therapyReportData?.report}
            templateData={therapyReportData?.template}
            setReport={() => {}}
            hideDownloadButton={true}
            hideSendToDoctorButton={true}
            hideHeader={true}
            hideHeadingText={true}
          />
        </ReportShell>
      )
    }

    if (reportType === 'assessment-report') {
      if (!assessmentReportData?.report) {
        return (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No assessment report is available for this booking yet.
          </Alert>
        )
      }

      return (
        <ReportShell
          title={assessmentReportData?.report?.title || 'Assessment Report'}
          subtitle={assessmentReportData?.report?.psychAssessmentTitle || 'Read-only support view'}
        >
          <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <AssessmentReportBody
              reportData={assessmentReportData?.report}
              orderedSectionsForPrint={[]}
            />
            {assessmentReportData?.report?.footer ? (
              <>
                <Divider sx={{ my: 3 }} />
                <AssessmentReportFooter reportData={assessmentReportData?.report} />
              </>
            ) : null}
          </Box>
        </ReportShell>
      )
    }

    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        No viewable report is configured for this event type yet.
      </Alert>
    )
  }

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : DRAWER_WIDTH,
          maxWidth: '100vw',
          height: isMobile ? '92vh' : '100%',
          borderTopLeftRadius: isMobile ? 18 : 0,
          borderTopRightRadius: isMobile ? 18 : 0,
          background:
            'linear-gradient(180deg, rgba(248, 250, 252, 0.98) 0%, rgba(241, 245, 249, 0.98) 100%)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.75, sm: 2.25 },
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            backgroundColor: 'rgba(255, 255, 255, 0.84)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <DescriptionOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {reportTitle}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: 'rgba(15, 23, 42, 0.72)' }}>
                Review the report in a read-only support view.
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap' }} useFlexGap>
                <HeaderChip label={eventTypeLabel} color="primary" />
                <HeaderChip label={resolvedBookedEvent?.patientName || resolvedBookedEvent?.userName || 'Patient'} />
                <HeaderChip label={resolvedBookedEvent?.mentorName || 'Practitioner'} />
                <HeaderChip label={clinicLabel} />
                <HeaderChip label={formatWhen(resolvedBookedEvent?.startTime)} />
              </Stack>
            </Box>
            <IconButton onClick={onClose} sx={{ color: '#475569' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, sm: 2.5 }, py: { xs: 2, sm: 3 } }}>
          {loading ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading report...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : (
            renderReport()
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
